import * as amplitude from '@amplitude/unified';
import { config } from './env';
import { redactDeep } from './redact';

/**
 * Amplitude: product analytics and session replay. One project per
 * environment (development, production), shared by every Slate client, so
 * the key here is whichever `ssm:fetch` materialized for this build. Local
 * builds use the development project.
 *
 * Initialized exactly once, from main.tsx, before the first render. Events
 * tracked before the SDK finishes initializing are queued, so the first
 * page's event never races it.
 *
 * Event names and property names are the shared vocabulary in
 * slate-api/ANALYTICS.md, copied rather than paraphrased: a dashboard built
 * on `Recording Started` counts a browser take and a Mac take alike. Nothing
 * about the content of a recording is ever sent: no titles, no transcripts,
 * no comment text, and no URLs from inside a recording. The share id, as
 * `video_id`, is the one identifier that travels.
 */

/** Which client this is. Stamped on every event here, never at a call site. */
const PLATFORM = 'web';

let started = false;
/** True only once the SDK has a key and has been initialized. */
let enabled = false;

export function initAmplitude(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  const key = config.amplitudeApiKey;
  if (!key) {
    console.warn('Amplitude API key missing, analytics disabled');
    return;
  }
  // Added before init so it is in place for the first event the SDK itself
  // sends, which is the one the pentest caught: the engagement plugin
  // reports the page it booted on, path and all.
  amplitude.add(redactingPlugin);
  amplitude.initAll(key, {
    analytics: { autocapture: false },
    sessionReplay: {
      sampleRate: 1,
      // The replay's own URL filter. It rewrites the page URL recorded with
      // each snapshot and interaction; a rule is a glob over the whole URL
      // and the first match wins, so the tokenized paths come first and the
      // query-borne codes after. Enabling interaction tracking is what turns
      // the filter on.
      interactionConfig: {
        enabled: true,
        batch: true,
        ugcFilterRules: [
          { selector: '*/r/*', replacement: '/r/[token]' },
          { selector: '*/invite/*', replacement: '/invite/[token]' },
          { selector: '*/start/*', replacement: '/start/[token]' },
          { selector: '*oobCode=*', replacement: '/reset-password' },
          { selector: '*session_id=*', replacement: '/settings/subscription' },
          { selector: '*code=*', replacement: '[redacted]' },
        ],
      },
    },
  });
  enabled = true;
}

/**
 * The last thing an event passes through before the network: any string in
 * its properties that carries a URL secret is redacted. Ours never do, by
 * the rule above; this is for the events the SDK's own plugins send.
 */
const redactingPlugin: Parameters<typeof amplitude.add>[0] = {
  name: 'slate-redact-urls',
  type: 'enrichment',
  execute: async (event) => {
    if (event.event_properties) event.event_properties = redactDeep(event.event_properties);
    if (event.user_properties) event.user_properties = redactDeep(event.user_properties);
    return event;
  },
};

/** Analytics is never allowed to break the thing it is measuring. */
function send(report: () => void): void {
  if (!enabled) return;
  try {
    report();
  } catch {
    // A dropped event is not worth an exception in a click handler.
  }
}

export type EventProperties = Record<string, unknown>;

/** One event, with `platform` added here so call sites never repeat it. */
export function track(name: string, properties: EventProperties = {}): void {
  send(() => amplitude.track(name, { platform: PLATFORM, ...properties }));
}

export interface Identity {
  /** The Slate account: the Postgres uuid from GET /v1/me, never the Firebase uid. */
  userId: string;
  email?: string | null;
  name?: string | null;
  plan?: 'free' | 'premium';
  planSource?: 'seat' | 'comp' | 'none';
  /** How many teams the person is on. */
  teams?: number;
}

/**
 * Who is holding this browser, the moment the Slate account is known. Called
 * again whenever the plan changes, so a dashboard split by plan follows the
 * account rather than whatever it was on the first page load.
 */
export function identify(identity: Identity): void {
  send(() => {
    amplitude.setUserId(identity.userId);
    const properties = new amplitude.Identify();
    if (identity.email) properties.set('email', identity.email);
    if (identity.name) properties.set('name', identity.name);
    if (identity.plan) properties.set('plan', identity.plan);
    if (identity.planSource) properties.set('plan_source', identity.planSource);
    if (identity.teams !== undefined) properties.set('teams', identity.teams);
    amplitude.identify(properties);
  });
}

/** Sign out: the next person on this machine is a new user. */
export function reset(): void {
  send(() => amplitude.reset());
}

export { amplitude };
